import type { CartItem } from '@/types'

import { formatIDR } from '@/lib/format'

// What every WhatsApp message builder receives from WhatsAppLink.
export interface MessageContext {
    // Site origin, known only in the browser; empty during the server render.
    origin: string
    // Store name from the admin settings.
    storeName: string
    // Order code for this order, generated in the browser after mount; empty before that.
    orderCode: string
}

// Accepts 0812..., 812..., +62 812-..., 62812... and returns digits in international format.
export const normalizeWhatsAppNumber = (raw: string | null | undefined): string | null => {
    if (!raw || /replace_me/i.test(raw)) return null

    const digits = raw.replace(/\D/g, '')

    if (digits.length < 8) return null
    if (digits.startsWith('0')) return `62${digits.slice(1)}`
    if (digits.startsWith('8') && digits.length <= 12) return `62${digits}`

    return digits
}

export const buildWhatsAppUrl = (message: string, rawNumber: string | null | undefined): string => {
    const number = normalizeWhatsAppNumber(rawNumber)
    const text = encodeURIComponent(message)

    return number
        ? `https://wa.me/${number}?text=${text}`
        : `https://wa.me/?text=${text}`
}

const describeItem = (item: { name: string, colorLabel: string | null }): string => {
    return item.colorLabel ? `${item.name} (warna ${item.colorLabel})` : item.name
}

const finish = (lines: Array<string>): string => {
    return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim()
}

export const buildCartMessage = (context: MessageContext, input: { items: Array<CartItem>, note: string, customerName: string }): string => {
    const lines = input.items.map((item, index) => {
        return [
            `${index + 1}. ${describeItem(item)}`,
            `   Harga: ${formatIDR(item.price)}`,
            `   Jumlah: ${item.quantity}`,
            `   Subtotal: ${formatIDR(item.price * item.quantity)}`
        ].join('\n')
    })

    const total = input.items.reduce((sum, item) => sum + item.price * item.quantity, 0)
    const count = input.items.reduce((sum, item) => sum + item.quantity, 0)
    const note = input.note.trim()
    const name = input.customerName.trim()

    return finish([
        `Halo ${context.storeName}! Saya ingin memesan:`,
        context.orderCode ? `Kode pesanan: ${context.orderCode}` : '',
        name ? `Nama: ${name}` : '',
        '',
        ...lines,
        '',
        `Total ${count} produk: ${formatIDR(total)}`,
        note ? `\nCatatan: ${note}` : '',
        '',
        'Mohon konfirmasi ketersediaan dan ongkos kirimnya ya. Terima kasih!'
    ])
}

export const buildProductMessage = (context: MessageContext, input: {
    name: string
    slug: string
    price: number
    quantity: number
    colorLabel: string | null
    madeToOrder: boolean
    note?: string
}): string => {
    const url = context.origin ? `${context.origin}/products/${input.slug}` : ''
    const note = input.note?.trim()

    return finish([
        `Halo ${context.storeName}! Saya ingin ${input.madeToOrder ? 'memesan custom' : 'memesan'}:`,
        context.orderCode ? `Kode pesanan: ${context.orderCode}` : '',
        '',
        `Produk: ${input.name}`,
        input.colorLabel ? `Warna: ${input.colorLabel}` : '',
        `Harga: ${formatIDR(input.price)}`,
        `Jumlah: ${input.quantity}`,
        `Total: ${formatIDR(input.price * input.quantity)}`,
        url,
        note ? `\nCatatan: ${note}` : '',
        '',
        input.madeToOrder
            ? 'Boleh kita diskusikan detail boneka custom saya? Terima kasih!'
            : 'Mohon konfirmasi ketersediaan dan ongkos kirimnya ya. Terima kasih!'
    ])
}

export const buildCustomRequestMessage = (context: MessageContext): string => {
    return [
        `Halo ${context.storeName}! Saya ingin memesan boneka rajut custom.`,
        '',
        'Ingin dibuat mirip siapa atau apa:',
        'Warna yang diinginkan:',
        'Ukuran:',
        'Dibutuhkan tanggal:',
        '',
        'Saya bisa kirim foto referensinya di sini. Terima kasih!'
    ].join('\n')
}

export const buildQuestionMessage = (context: MessageContext): string => {
    return `Halo ${context.storeName}! Saya ingin bertanya tentang produk rajutan handmade kalian.`
}
